import ManagerHotelConfig from "./pages/ManagerHotelConfig";
import StaffManagementTab from "./pages/ManagerStaffTab/StaffManagementTab";
import StaffManagementModule from "./pages/ManagerStaffTab/StaffManagementModule";
import RoomManagementModule from "./pages/ManagerRoomTab/RoomManagementModule";
import InventoryManagementModule from "./pages/ManagerInventoryTab/InventoryManagementModule";
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./styles/admin-theme.css";
import "./App.css";

// Authentication
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ManagerDashboard from "./pages/ManagerDashboard";
import HotelOwnerDashboard from "./pages/HotelOwnerDashboard";
import HotelRegistration from "./pages/HotelRegistration";
import AdminHotelApprovals from "./pages/AdminHotelApprovals";
import AdminHotelApprovalDetails from "./pages/AdminHotelApprovalDetails";
import HotelStatusPage from "./pages/HotelStatusPage";
import OwnerSettingsPage from "./pages/OwnerSettingsPage";
import MyProperties from "./pages/MyProperties";
import HotelManagement from "./pages/HotelManagement";
import HotelDetails from "./pages/HotelDetails";
import UserManagement from "./pages/UserManagement";
import NotificationManagement from "./pages/NotificationManagement";
import PropertyDetail from "./pages/PropertyDetailNew";
import FlightsPage from "./pages/FlightsPage";
import CarsPage from "./pages/CarsPage";
import AttractionsPage from "./pages/AttractionsPage";
import TaxisPage from "./pages/TaxisPage";
import ProfilePage from "./pages/ProfilePage";
import IoTManagement from "./pages/IoTManagement";
import IoTManagerDashboard from "./pages/IoTManagerDashboard";
import ManagerIoTDashboard from "./pages/ManagerIoTDashboard";
import RoomControls from "./pages/RoomControls";
import DeviceDetails from "./pages/DeviceDetails";
import About from "./pages/About";
import HomePage from "./pages/HomePage";
import BookingPage from "./pages/BookingPage";
import PublicBookingPage from "./pages/PublicBookingPage";
import GuestBookingPage from "./pages/GuestBookingPage";
import HotelDetailsPage from "./pages/HotelDetailsPage";
import MyBookingsPage from "./pages/MyBookingsPage";
import BookingDetailPage from "./pages/BookingDetailPage";
import GuestProfilePage from "./pages/GuestProfilePage";
import GuestDashboard from "./pages/GuestDashboard";
import CheckoutPage from "./pages/CheckoutPage";
import BookingRefPage from "./pages/BookingRefPage";

// Components
import Header from "./components/Header";
import GuestLayout from "./components/GuestLayout";
import Footer from "./components/Footer";
import EmailVerification from "./components/EmailVerification";
import PasswordlessSignIn from "./components/PasswordlessSignIn";
import HotelOwnerRouteGuard from "./components/HotelOwnerRouteGuard";
import PublicPageLayout from "./components/PublicPageLayout";

// Existing pages (keeping them for backward compatibility)
import RoomsPage from "./pages/RoomsPage";
import ChatPage from "./pages/ChatPage";

function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="App">
          {/* Navbar/Header handled by PublicPageLayout or role layouts */}
          <Routes>
            {/* Manager Hotel Config Route */}
            <Route path="/manager/hotel" element={
              <ProtectedRoute requiredRole="manager">
                <ManagerHotelConfig />
              </ProtectedRoute>
            } />
            {/* Public booking reference — no login required */}
            <Route path="/booking/ref/:token" element={<BookingRefPage />} />

            {/* Public Routes */}
            <Route path="/login" element={
              <PublicPageLayout>
                <Login />
              </PublicPageLayout>
            } />
            <Route path="/signup" element={
              <PublicPageLayout>
                <Signup />
              </PublicPageLayout>
            } />
            <Route path="/passwordless" element={
              <PublicPageLayout>
                <PasswordlessSignIn />
              </PublicPageLayout>
            } />
            <Route path="/auth/verify-email" element={
              <PublicPageLayout>
                <EmailVerification />
              </PublicPageLayout>
            } />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            
            {/* Admin Dashboard Route */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            
            {/* Manager Dashboard Route */}
            <Route path="/manager" element={
              <ProtectedRoute>
                <ManagerDashboard />
              </ProtectedRoute>
            } />
            {/* Hotel Owner Dashboard Routes */}
            <Route path="/owner/*" element={
              <ProtectedRoute>
                <HotelOwnerRouteGuard>
                  <HotelOwnerDashboard />
                </HotelOwnerRouteGuard>
              </ProtectedRoute>
            } />
            
            <Route path="/owner/register" element={
              <ProtectedRoute>
                <HotelOwnerRouteGuard>
                  <HotelRegistration />
                </HotelOwnerRouteGuard>
              </ProtectedRoute>
            } />
            
            <Route path="/owner/status" element={
              <ProtectedRoute>
                <HotelOwnerRouteGuard>
                  <HotelStatusPage />
                </HotelOwnerRouteGuard>
              </ProtectedRoute>
            } />
            
            <Route path="/owner/settings" element={
              <ProtectedRoute>
                <HotelOwnerRouteGuard>
                  <OwnerSettingsPage />
                </HotelOwnerRouteGuard>
              </ProtectedRoute>
            } />
            
            
            {/* Future owner routes (properties, bookings, etc.) will be guarded by requireApproval */}
            <Route path="/owner/*" element={
              <ProtectedRoute>
                <HotelOwnerRouteGuard requireApproval={true}>
                  <HotelOwnerDashboard />
                </HotelOwnerRouteGuard>
              </ProtectedRoute>
            } />
            
            {/* Admin Management Routes */}
            <Route path="/admin/hotel" element={
              <ProtectedRoute>
                <HotelManagement />
              </ProtectedRoute>
            } />

            <Route path="/admin/hotel/:id" element={
              <ProtectedRoute>
                <HotelDetails />
              </ProtectedRoute>
            } />

            <Route path="/admin/approvals" element={
              <ProtectedRoute>
                <AdminHotelApprovals />
              </ProtectedRoute>
            } />
            
            <Route path="/admin/approvals/:id" element={
              <ProtectedRoute>
                <AdminHotelApprovalDetails />
              </ProtectedRoute>
            } />
            
            <Route path="/admin/users" element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            } />
            
            <Route path="/admin/notifications" element={
              <ProtectedRoute>
                <NotificationManagement />
              </ProtectedRoute>
            } />
            
            {/* IoT Management Routes */}
            <Route path="/iot-management" element={
              <ProtectedRoute>
                <IoTManagement />
              </ProtectedRoute>
            } />
            
            {/* Device Details Route */}
            <Route path="/device-details/:deviceId" element={
              <ProtectedRoute>
                <DeviceDetails />
              </ProtectedRoute>
            } />
            
            {/* Manager IoT Dashboard Route */}
            <Route path="/manager/iot" element={
              <ProtectedRoute>
                <IoTManagerDashboard />
              </ProtectedRoute>
            } />
            
            {/* New Manager IoT Device Management Route */}
            <Route path="/manager/iot-devices" element={
              <ProtectedRoute requiredRole="manager">
                <ManagerIoTDashboard />
              </ProtectedRoute>
            } />
            
            {/* Manager Staff Management Route */}
            <Route path="/manager/staff" element={
              <ProtectedRoute requiredRole="manager">
                <StaffManagementModule />
              </ProtectedRoute>
            } />

            {/* Manager Room Management Route */}
            <Route path="/manager/rooms" element={
              <ProtectedRoute requiredRole="manager">
                <RoomManagementModule />
              </ProtectedRoute>
            } />

            {/* Manager Inventory Management Route */}
            <Route path="/manager/inventory" element={
              <ProtectedRoute requiredRole="manager">
                <InventoryManagementModule />
              </ProtectedRoute>
            } />
            
            <Route path="/room-controls" element={
              <ProtectedRoute>
                <RoomControls userRoomId="default-room-id" />
              </ProtectedRoute>
            } />
            
            {/* Main Home Route */}
            <Route path="/" element={
              <PublicPageLayout>
                <HomePage />
                <Footer />
              </PublicPageLayout>
            } />
            
            {/* Property Detail Route */}
            <Route path="/property/:id" element={
              <PublicPageLayout>
                <PropertyDetail />
                <Footer />
              </PublicPageLayout>
            } />

            <Route path="/flights" element={
              <PublicPageLayout>
                <FlightsPage />
                <Footer />
              </PublicPageLayout>
            } />

            <Route path="/cars" element={
              <PublicPageLayout>
                <CarsPage />
                <Footer />
              </PublicPageLayout>
            } />

            <Route path="/attractions" element={
              <PublicPageLayout>
                <AttractionsPage />
                <Footer />
              </PublicPageLayout>
            } />

            <Route path="/taxis" element={
              <PublicPageLayout>
                <TaxisPage />
                <Footer />
              </PublicPageLayout>
            } />
            
            <Route path="/rooms" element={
              <ProtectedRoute>
                <div>
                  <Header />
                  <main className="main-content">
                    <RoomsPage />
                  </main>
                  <Footer />
                </div>
              </ProtectedRoute>
            } />
            
            {/* Public Booking Page: /booking */}
            <Route path="/booking" element={<PublicBookingPage />} />

            {/* Guest Booking Page: /guest/booking (guest only) */}
            <Route path="/guest/booking" element={<ProtectedRoute requiredRole="guest"><GuestBookingPage /></ProtectedRoute>} />

            {/* Checkout Page: guest only */}
            <Route path="/checkout/:bookingId" element={<ProtectedRoute requiredRole="guest"><GuestLayout><CheckoutPage /></GuestLayout></ProtectedRoute>} />

            {/* Guest Dashboard and routes: guest only */}
            <Route path="/guest/dashboard" element={<ProtectedRoute requiredRole="guest"><GuestLayout><GuestDashboard /></GuestLayout></ProtectedRoute>} />
            <Route path="/my-bookings" element={<ProtectedRoute requiredRole="guest"><GuestLayout><MyBookingsPage /></GuestLayout></ProtectedRoute>} />
            <Route path="/booking-details/:bookingId" element={<ProtectedRoute requiredRole="guest"><GuestLayout><BookingDetailPage /></GuestLayout></ProtectedRoute>} />
            <Route path="/guest-profile" element={<ProtectedRoute requiredRole="guest"><GuestLayout><GuestProfilePage /></GuestLayout></ProtectedRoute>} />

            {/* Admin Dashboard: admin only */}
            <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />

            {/* Manager Dashboard: manager only */}
            <Route path="/manager" element={<ProtectedRoute requiredRole="manager"><ManagerDashboard /></ProtectedRoute>} />

            {/* Owner Dashboard: owner only */}
            <Route path="/owner/*" element={<ProtectedRoute requiredRole="owner"><HotelOwnerRouteGuard><HotelOwnerDashboard /></HotelOwnerRouteGuard></ProtectedRoute>} />

            <Route path="/chat" element={
              <ProtectedRoute>
                <div>
                  <Header />
                  <main className="main-content">
                    <ChatPage />
                  </main>
                  <Footer />
                </div>
              </ProtectedRoute>
            } />

            <Route path="/profile" element={
              <ProtectedRoute>
                <div>
                  <Header />
                  <main className="main-content">
                    <ProfilePage />
                  </main>
                  <Footer />
                </div>
              </ProtectedRoute>
            } />
            
            {/* New Guest Routes */}
            <Route path="/about" element={
              <PublicPageLayout>
                <About />
                <Footer />
              </PublicPageLayout>
            } />
            <Route path="/hotels/:hotelId" element={
              <PublicPageLayout>
                <HotelDetailsPage />
                <Footer />
              </PublicPageLayout>
            } />
            
            {/* Redirect unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Toast Notifications */}
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
